class DlPageScrollTextBannerH extends HTMLElement {
    constructor() {
        super();
        this.container = null;
        this.media = null;
        this.contentMain = null;
        this.scrollTextItems = null;
        this.videoButton = null;
        this.videoPops = null;
        this.resizeObserver = null;
        this.scrollHandler = null;
        this.ticking = false;
        this.boundHandleResize = null;
        this.boundViewportChange = null;
    }

    connectedCallback() {
        this.container = this.querySelector('.dl-page-scroll-text-banner-h');
        this.media = this.querySelector('.dl-page-scroll-text-banner-h-media');
        this.contentMain = this.querySelector('.dl-page-scroll-text-banner-h-content-main');
        this.scrollTextItems = this.querySelectorAll('.dl-page-scroll-text-banner-h-content-scroll-text-item');
        this.videoButton = this.querySelector('.dl-page-scroll-text-banner-h-content-main-btn');
        this.videoPops = this.querySelectorAll('dl-pop-video-h');

        if (!this.container || !this.contentMain) return;

        this.bindVideoButtonEvent();

        this.boundHandleResize = this.handleResize.bind(this);
        this.boundViewportChange = this.handleViewportChange.bind(this);
        this.scrollHandler = this.handleScroll.bind(this);

        this.updateLayout();

        window.addEventListener('resize', this.boundHandleResize);
        window.addEventListener('scroll', this.scrollHandler, { passive: true });

        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', this.boundViewportChange);
            window.visualViewport.addEventListener('scroll', this.boundViewportChange);
        }

        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(() => this.updateLayout());
        }

        this.resizeObserver = new ResizeObserver(() => {
            this.updateContentOffset();
        });
        this.resizeObserver.observe(this.contentMain);

        this.handleScroll();
    }

    bindVideoButtonEvent() {
        if (this.videoButton && this.videoPops && this.videoPops.length > 0) {
            this.videoButton.addEventListener('click', () => {
                const isMobile = window.innerWidth < 768;

                let videoToShow;
                if (isMobile) {
                    videoToShow = this.videoPops.length > 1 ? this.videoPops[1] : this.videoPops[0];
                } else {
                    videoToShow = this.videoPops[0];
                }

                if (videoToShow) {
                    videoToShow.show();
                }
            });
        }
    }

    disconnectedCallback() {
        if (this.boundHandleResize) {
            window.removeEventListener('resize', this.boundHandleResize);
        }
        window.removeEventListener('scroll', this.scrollHandler);
        if (window.visualViewport && this.boundViewportChange) {
            window.visualViewport.removeEventListener('resize', this.boundViewportChange);
            window.visualViewport.removeEventListener('scroll', this.boundViewportChange);
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    handleResize() {
        this.updateLayout();
        this.handleScroll();
    }

    handleViewportChange() {
        this.updateLayout();
    }

    getExtraHeight() {
        const raw = getComputedStyle(document.documentElement).getPropertyValue('--extra-height').trim();
        const parsed = parseFloat(raw);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    parseAspectRatio(value) {
        if (!value) return null;
        const trimmed = value.trim();
        if (trimmed.includes('/')) {
            const parts = trimmed.split('/').map((part) => parseFloat(part.trim()));
            if (parts.length === 2 && parts[0] && parts[1]) return parts[0] / parts[1];
        }
        const numeric = parseFloat(trimmed);
        return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
    }

    updateLayout() {
        this.updateViewportHeight();
        this.syncMediaHeight();
        this.updateContentOffset();
    }

    updateViewportHeight() {
        this.style.setProperty('--dl-scroll-banner-viewport-height', `${window.innerHeight}px`);
    }

    syncMediaHeight() {
        const extraHeight = this.getExtraHeight();
        const viewportHeight = Math.max(window.innerHeight, window.visualViewport?.height || 0);
        const isMobile = window.matchMedia('(max-width: 990px)').matches;
        const styles = getComputedStyle(this);
        const aspectValue = isMobile
            ? styles.getPropertyValue('--aspect-ratio-m').trim()
            : styles.getPropertyValue('--aspect-ratio').trim();
        const aspectRatio = this.parseAspectRatio(aspectValue);

        let mediaHeight = viewportHeight - extraHeight;

        if (aspectRatio) {
            mediaHeight = Math.min(window.innerWidth / aspectRatio, mediaHeight);
        }

        mediaHeight = Math.round(mediaHeight);
        this.style.setProperty('--dl-scroll-banner-media-height', `${mediaHeight}px`);

        if (this.media) {
            this.media.style.height = `${mediaHeight}px`;
        }
    }

    updateContentOffset() {
        if (!this.container || !this.contentMain) return;

        const contentMainHeight = this.contentMain.offsetHeight;
        this.container.style.setProperty('--content-offset', `-${contentMainHeight}px`);
    }

    handleScroll() {
        if (!this.ticking) {
            window.requestAnimationFrame(() => {
                this.syncMediaHeight();
                this.updateScrollEffects();
                this.ticking = false;
            });
            this.ticking = true;
        }
    }

    updateScrollEffects() {
        const scrollY = window.pageYOffset;
        const componentRect = this.getBoundingClientRect();
        const componentTop = scrollY + componentRect.top;
        const windowHeight = window.innerHeight;

        if (this.contentMain && this.scrollTextItems && this.scrollTextItems.length > 0) {
            const firstItem = this.scrollTextItems[0];
            const firstItemRect = firstItem.getBoundingClientRect();
            const itemHeight = firstItemRect.height;
            const itemTop = firstItemRect.top;

            let opacity = 1;

            if (itemTop < windowHeight) {
                const visibleHeight = Math.min(windowHeight - itemTop, itemHeight);
                const visiblePercent = visibleHeight / itemHeight;
                opacity = Math.max(0, 1 - (visiblePercent / 0.5));
            }

            this.contentMain.style.opacity = opacity;
        }

        if (this.container) {
            const fadeDistance = 500;
            const scrollProgress = Math.max(0, Math.min(1, (scrollY - componentTop) / fadeDistance));
            const overlayOpacity = Math.min(0.6, scrollProgress * 0.6);
            this.container.style.setProperty('--overlay-opacity', overlayOpacity);
        }

        if (this.scrollTextItems) {
            this.scrollTextItems.forEach((item) => {
                const itemRect = item.getBoundingClientRect();
                const itemHeight = itemRect.height;
                const currentOpacity = parseFloat(getComputedStyle(item).opacity);
                const isCurrentlyVisible = currentOpacity > 0.5;
                const unshiftedBottom = itemRect.bottom - itemHeight * 0.5;

                let shouldShow = false;

                if (!isCurrentlyVisible) {
                    shouldShow = unshiftedBottom <= windowHeight || unshiftedBottom < 0;
                } else {
                    shouldShow = itemRect.bottom <= windowHeight || itemRect.bottom < 0;
                }

                if (shouldShow) {
                    item.style.opacity = '1';
                    item.style.transform = 'translateY(0)';
                } else {
                    item.style.opacity = '0';
                    item.style.transform = 'translateY(50%)';
                }
            });
        }
    }
}
customElements.define('dl-page-scroll-text-banner-h', DlPageScrollTextBannerH);
